import API from "../utils/api";
const electronApi = window.electron ? window.electron.ipcRenderer : null;
const ipcRenderer = window.electron ? window.electron.ipcRenderer : null;
// src/utils/biometricoAPI.js
/**
 * API Service para manejo del dispositivo biométrico MB160
 * Proporciona métodos para conectar, capturar y gestionar huellas digitales
 */




/**
 * Clase para manejo de API biométrica
 */
class BiometricoAPI {
  constructor() {
    this.deviceConfig = {
      ip: localStorage.getItem('biometrico_ip') || '192.168.1.201',
      port: localStorage.getItem('biometrico_port') || '4370',
      timeout: 10000 // 10 segundos
    };

    // Verificar si estamos en un entorno Electron
    this.isElectron = !!electronApi;
    if (!this.isElectron) {
      console.log('Entorno Electron no detectado, algunas funciones no estarán disponibles');
    }
  }
  setConnectionParams(params = {}) {
    if (params.ip) {
      this.deviceConfig.ip = params.ip;
      localStorage.setItem('biometrico_ip', params.ip);
    }

    if (params.port) {
      this.deviceConfig.port = params.port;
      localStorage.setItem('biometrico_port', params.port);
    }

    if (params.timeout) {
      this.deviceConfig.timeout = params.timeout;
    }

    return this.deviceConfig;
  }
  getConnectionParams() {
    return { ...this.deviceConfig };
  }
  /**
   * Conectar con el dispositivo biométrico
   * @returns {Promise<Object>} Resultado de la conexión
   */
  // En biometricoAPI.js
  async connectDevice(params = {}) {
    // Si se proporcionan nuevos parámetros, actualizarlos
    if (params.ip || params.port) {
      this.setConnectionParams(params);
    }


    try {
      // Verificación más explícita
      if (this.isElectron) {
        console.log(`Conectando a ${this.deviceConfig.ip}:${this.deviceConfig.port} via IPC`);


        try {
          // Pasar la configuración al proceso principal
          const response = await electronApi.invoke('biometrico:connect', this.deviceConfig);

          console.log('Respuesta IPC:', response);

          if (response.success) {
            return {
              success: true,
              deviceInfo: {
                ...response.deviceInfo,
                ipAddress: this.deviceConfig.ip,
                port: this.deviceConfig.port
              }
            };
          } else {
            console.log('Respuesta IPC con error:', response);
            throw new Error(response.message || 'Error al conectar al dispositivo');
          }
        } catch (error) {
          console.error('Error en comunicación IPC:', error);
          throw error;
        }
      } else {
        console.log('ipcRenderer no disponible, usando API REST');
        return await this.connectThroughServer();
      }
    } catch (error) {
      console.error('Error en conexión con biométrico:', error);
      return {
        success: false,
        message: `Error de comunicación: ${error.message}`
      };
    }
  }

  /**
   * Método alternativo para conectar a través del servidor
   * @returns {Promise<Object>} Resultado de la conexión
   */
  async connectThroughServer() {
    try {
      const response = await fetch('/api/biometrico/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ip: this.deviceConfig.ip,
          port: this.deviceConfig.port,
          timeout: this.deviceConfig.timeout
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error en conexión con servidor:', error);
      return {
        success: false,
        message: 'Error de comunicación con el servidor'
      };
    }
  }

  /**
   * Desconectar el dispositivo biométrico
   * @returns {Promise<Object>} Resultado de la desconexión
   */
  async disconnectDevice() {
    try {
      // Intentar desconectar a través de IPC
      const response = await ipcRenderer.invoke('biometrico:disconnect');

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        return await API.fetch('/biometrico/disconnect', {
          method: 'POST'
        });
      }

      return response;
    } catch (error) {
      console.error('Error al desconectar biométrico:', error);
      return { success: false, message: 'Error al desconectar el dispositivo' };
    }
  }

  /**
   * Obtener las huellas registradas de un usuario
   * @param {number} userId - ID del usuario
   * @returns {Promise<Array>} Lista de huellas del usuario
   */
  async getUserFingerprints(userId) {
    try {
      const response = await API.fetch('/biometrico/huellas', {
        method: 'POST',
        body: JSON.stringify({ idUsuario: userId }),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.success) {
        return response.huellas || [];
      }

      return [];
    } catch (error) {
      console.error('Error al obtener huellas:', error);
      return [];
    }
  }

  /**
   * Capturar una huella en el dispositivo
   * @returns {Promise<Object>} Resultado de la captura
   */
  async captureFingerprint() {
    try {
      // Intentar capturar a través de IPC
      const response = await ipcRenderer.invoke('biometrico:capture');

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        return await API.fetch('/biometrico/capture', {
          method: 'POST'
        });
      }

      return response;
    } catch (error) {
      console.error('Error en captura de huella:', error);
      return {
        success: false,
        message: 'Error en la comunicación durante la captura de huella'
      };
    }
  }

  /**
   * Finalizar el proceso de registro de huella
   * @param {Object} data - Datos para finalizar el registro
   * @param {number} data.userId - ID del usuario
   * @param {number} data.fingerIndex - Índice del dedo (1-10)
   * @param {string} data.userName - Nombre del usuario
   * @returns {Promise<Object>} Resultado del registro
   */
  async finalizeEnrollment(data) {
    try {
      // Intentar finalizar a través de IPC
      const response = await ipcRenderer.invoke('biometrico:finalize-enrollment', data);

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        return await API.fetch('/biometrico/enroll', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      return response;
    } catch (error) {
      console.error('Error al finalizar registro:', error);
      return {
        success: false,
        message: 'Error al registrar la huella en la base de datos'
      };
    }
  }

  /**
   * Verificar una huella capturada contra la base de datos
   * @param {Object} data - Datos para la verificación
   * @param {number} data.userId - ID del usuario
   * @param {string} data.templateData - Datos de la plantilla de huella capturada
   * @returns {Promise<Object>} Resultado de la verificación
   */
  async verifyFingerprint(data) {
    try {
      // Intentar verificar a través de IPC
      const response = await ipcRenderer.invoke('biometrico:verify', data);

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        return await API.fetch('/biometrico/verify', {
          method: 'POST',
          body: JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      return response;
    } catch (error) {
      console.error('Error en verificación de huella:', error);
      return {
        success: false,
        message: 'Error durante la verificación de huella'
      };
    }
  }

  /**
   * Eliminar una huella de la base de datos
   * @param {number} fingerprintId - ID de la huella a eliminar
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  async deleteFingerprint(fingerprintId) {
    try {
      return await API.fetch('/biometrico/delete', {
        method: 'POST',
        body: JSON.stringify({ idFingerprint: fingerprintId }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error al eliminar huella:', error);
      return {
        success: false,
        message: 'Error al eliminar la huella de la base de datos'
      };
    }
  }

  /**
   * Cancelar cualquier operación en curso
   * @returns {Promise<Object>} Resultado de la cancelación
   */
  async cancelOperation() {
    try {
      // Intentar cancelar a través de IPC
      const response = await ipcRenderer.invoke('biometrico:cancel');

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        return await API.fetch('/biometrico/cancel', {
          method: 'POST'
        });
      }

      return response;
    } catch (error) {
      console.error('Error al cancelar operación:', error);
      return {
        success: false,
        message: 'Error al cancelar la operación'
      };
    }
  }

  /**
   * Obtener lista de dispositivos disponibles
   * @returns {Promise<Array>} Lista de dispositivos
   */
  async getAvailableDevices() {
    try {
      // Intentar obtener a través de IPC
      const response = await ipcRenderer.invoke('biometrico:list-devices');

      // Si no se pudo por IPC, intentar a través del servidor
      if (!response.success) {
        const apiResponse = await API.fetch('/biometrico/list-devices', {
          method: 'GET'
        });

        return apiResponse.success ? apiResponse.devices : [];
      }

      return response.devices || [];
    } catch (error) {
      console.error('Error al listar dispositivos:', error);
      return [];
    }
  }
}

export default new BiometricoAPI();